import { useCallback } from 'react';
import { toast } from 'sonner';

import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { AssetActionHandlers } from '@/sections/asset/types';
import { Asset } from '@/types';

import { useAssetScanHandlers } from './useAssetScanHandlers';
import {
  useAssetStatusHandlers,
  UseAssetStatusHandlersParams,
} from './useAssetStatusHandlers';

export interface UseAssetActionHandlersParams {
  /** Forwarded to useAssetStatusHandlers for bulk path detection */
  selectionState?: BulkSelectionState;
  /** Forwarded to useAssetStatusHandlers — called when bulk is required */
  onBulkRequested?: UseAssetStatusHandlersParams['onBulkRequested'];
}

/**
 * Main coordinator hook for asset action handlers
 * Composes scan and status handlers with share functionality
 *
 * @returns Combined action handlers interface
 */
export const useAssetActionHandlers = ({
  selectionState,
  onBulkRequested,
}: UseAssetActionHandlersParams = {}): AssetActionHandlers => {
  const { handleScan } = useAssetScanHandlers();
  const { handleUpdateStatus } = useAssetStatusHandlers({
    selectionState,
    onBulkRequested,
  });

  /**
   * Shares asset URLs by copying them to clipboard
   * Generates deep links with drawer state
   *
   * @param assets - Assets to share
   */
  const handleShare = useCallback((assets: Asset[]) => {
    const urls = assets.map(asset => {
      // Extract base path up to userId (e.g., "/encoded-user-id")
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const basePath = pathParts[0] ? `/${pathParts[0]}` : '';

      const params = new URLSearchParams();
      params.append('assetDrawerKey', asset.key);
      params.append('drawerOrder', JSON.stringify(['asset']));
      return `${window.location.origin}${basePath}/assets?${params.toString()}`;
    });

    navigator.clipboard.writeText(urls.join('\n'));
    toast.success(
      `Copied ${urls.length} asset URL${urls.length > 1 ? 's' : ''} to clipboard`
    );
  }, []);

  return {
    handleScan,
    handleUpdateStatus,
    handleShare,
  };
};
